import { useState, useEffect, useCallback, useMemo } from 'react';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import type { TimeSelection } from '@/shared/ui/time-range-selector';

const STORAGE_KEY = 'global-time-range-selection';

/**
 * Global time range hook that persists selection across reloads
 * and shares state across all components using it.
 * 
 * This hook is useful for dashboards and views that need consistent time ranges
 * across different modules.
 */
export function useGlobalTimeRange() {
  const [timeSelection, setTimeSelection] = useState<TimeSelection>(() => {
    // Try to load from localStorage on initial mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert date strings back to Date objects
        if (parsed.mode === 'preset') {
          return {
            mode: 'preset',
            preset: parsed.preset,
            refDate: new Date(parsed.refDate),
          };
        } else if (parsed.mode === 'custom') {
          return {
            mode: 'custom',
            start: new Date(parsed.start),
            end: new Date(parsed.end),
          };
        }
      }
    } catch (error) {
      console.error('Failed to load time range from localStorage:', error);
    }
    
    // Default: today
    return {
      mode: 'preset',
      preset: 'day',
      refDate: new Date(),
    };
  });

  // Persist to localStorage whenever selection changes
  useEffect(() => {
    try {
      const toStore = timeSelection.mode === 'preset'
        ? {
            mode: 'preset',
            preset: timeSelection.preset,
            refDate: timeSelection.refDate.toISOString(),
          }
        : {
            mode: 'custom',
            start: timeSelection.start.toISOString(),
            end: timeSelection.end.toISOString(),
          };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch (error) {
      console.error('Failed to save time range to localStorage:', error);
    }
  }, [timeSelection]);

  // Listen for storage events to sync across tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.mode === 'preset') {
            setTimeSelection({
              mode: 'preset',
              preset: parsed.preset,
              refDate: new Date(parsed.refDate),
            });
          } else if (parsed.mode === 'custom') {
            setTimeSelection({
              mode: 'custom',
              start: new Date(parsed.start),
              end: new Date(parsed.end),
            });
          }
        } catch (error) {
          console.error('Failed to sync time range from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const updateTimeSelection = useCallback((newSelection: TimeSelection) => {
    setTimeSelection(newSelection);
  }, []);

  // Helper to get the actual start and end dates
  const getDateRange = useCallback((): { start: Date; end: Date } => {
    if (timeSelection.mode === 'preset') {
      switch (timeSelection.preset) {
        case 'day':
          return { start: startOfDay(timeSelection.refDate), end: endOfDay(timeSelection.refDate) };
        case 'week':
          return {
            start: startOfWeek(timeSelection.refDate, { weekStartsOn: 1 }),
            end: endOfWeek(timeSelection.refDate, { weekStartsOn: 1 })
          };
        case 'month':
          return {
            start: startOfMonth(timeSelection.refDate),
            end: endOfMonth(timeSelection.refDate)
          };
        default:
          return { start: startOfDay(timeSelection.refDate), end: endOfDay(timeSelection.refDate) };
      }
    }
    return { start: timeSelection.start, end: timeSelection.end };
  }, [timeSelection]);

  // Memoize the dateRange object to prevent unnecessary re-renders
  const dateRange = useMemo(() => getDateRange(), [getDateRange]);

  return {
    timeSelection,
    setTimeSelection: updateTimeSelection,
    dateRange,
  };
}
